/**
 * NEON COACH - مخزن الحالة المركزية التفاعلي (State Store)
 * يدير التخزين المحلي الآمن عبر localStorage مع استدعاء المشتركين وحفظ البيانات تلقائياً
 */

import { EMPTY_INITIAL_STATE } from './demoData.js';
import { calculateAge, calculateNutritionTargets, filterStrongestExercisePerMuscle } from '../domain/calculations.js';
import { syncService } from '../services/syncService.js';
import { executeActionTools, shoppingItemsFor, localDate } from '../domain/actionAgent.js';
import { rolloverDailyState } from '../domain/dailyCycle.js';
import { getOrCreateGuestUserId } from '../utils/userId.js';

import { estimateAdaptiveExpenditure, dayNumber, normalizeWeights, distributeWeeklyCalories, allocateMacros } from '../domain/metabolicEngine.js';
import { metabolicInputs } from '../domain/metabolicState.js';
import { wtLoad } from '../domain/compositionEstimate.js';

const STORAGE_KEY = 'neon_coach_app_state_v1';

function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

class Store {
  constructor() {
    this.state = this.loadState();
    this.listeners = new Set();
    syncService.setStore(this);
    const rolledOver = this.ensureCurrentDay({ notify: false });
    this.initDailyStackSync();
    if (rolledOver) this.saveState({ notify: false });
    this.startDailyRolloverMonitor();
  }

  ensureCurrentDay({ now = new Date(), notify = true } = {}) {
    const result = rolloverDailyState(this.state, now);
    if (!result.changed) return false;
    this.state = result.state;
    const budget = this.state.metabolicData?.weeklyCalories;
    if (budget?.length === 7 && this.state.userProfile?.targetCalories > 0) {
      const index = (new Date(`${this.state.today.date}T12:00:00`).getDay() + 1) % 7;
      const targets = { targetCalories: budget[index], ...allocateMacros(budget[index], this.state.userProfile) };
      Object.assign(this.state.today, { targetCalories: targets.targetCalories, targetProtein: targets.protein, targetCarbs: targets.carbs, targetFats: targets.fats });
    }
    this.saveState({ notify });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('neon:day-rollover', {
        detail: { date: this.state.today.date, archivedDate: result.archived?.date || null }
      }));
    }
    return true;
  }

  startDailyRolloverMonitor() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const schedule = () => {
      if (this._dailyRolloverTimer) window.clearTimeout(this._dailyRolloverTimer);
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 0, 100);
      this._dailyRolloverTimer = window.setTimeout(() => {
        this.ensureCurrentDay();
        schedule();
      }, Math.max(250, nextMidnight.getTime() - now.getTime()));
    };
    window.addEventListener('focus', () => this.ensureCurrentDay());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.ensureCurrentDay();
    });
    schedule();
  }

  initDailyStackSync() {
    try {
      if (typeof localStorage !== 'undefined') {
        const items = this.getDailyStackItems();
        this.syncSupplementsToSchedule(items);
      }
    } catch (e) {}
  }

  loadState() {
    try {
      if (typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          // رفض الحالات القديمة التي تحتوي على بيانات وضع تجريبي
          if (parsed && parsed.auth?.user?.id !== 'demo_user_01') {
            // تنظيف أي بيانات وهمية قديمة لتقرير التقدم من الوضع التجريبي السابق (مثل 129 كغ أو InBody 770 Clinic)
            if (parsed.progressReport) {
              const rep = parsed.progressReport;
              // Numeric measurements are real data, never evidence of a demo account.
              const isLegacyMock = rep.isDemo === true;

              if (isLegacyMock) {
                parsed.progressReport = JSON.parse(JSON.stringify(EMPTY_INITIAL_STATE.progressReport));
                try {
                  localStorage.removeItem('po_coach_weights');
                } catch {}
              }
            }
            if (!Array.isArray(parsed.favoriteRecipeIds)) {
              parsed.favoriteRecipeIds = [];
            }
            return parsed;
          }
          // مسح الجلسة التجريبية القديمة
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (e) {
      console.warn('تعذر قراءة الحالة من التخزين المحلي، سيتم استخدام الحالة الافتراضية:', e);
    }
    return JSON.parse(JSON.stringify(EMPTY_INITIAL_STATE));
  }

  saveState({ notify = true } = {}) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      }
      if (notify) this.notify();
    } catch (e) {
      console.error('تعذر حفظ الحالة في التخزين المحلي:', e);
    }
  }

  resetState() {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    this.state = JSON.parse(JSON.stringify(EMPTY_INITIAL_STATE));
    this.saveState();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }

  getState() {
    this.ensureCurrentDay({ notify: false });
    return this.state;
  }

  setState(partialState, { notify = true } = {}) {
    if (!partialState || typeof partialState !== 'object') return;
    this.ensureCurrentDay({ notify: false });
    if (Array.isArray(partialState.loggedMeals)) {
      const dates = new Set([...this.state.loggedMeals || [], ...partialState.loggedMeals].map(m => m.date));
      const signature = (meals, date) => JSON.stringify(meals.filter(m => m.date === date).map(m => [m.id, Number(m.calories), Number(m.protein), Number(m.carbs), Number(m.fats)]).sort((a,b)=>String(a[0]).localeCompare(String(b[0]))));
      for (const date of dates) if (signature(this.state.loggedMeals || [], date) !== signature(partialState.loggedMeals, date)) this.invalidateNutritionDay(date);
    }
    this.state = {
      ...this.state,
      ...partialState
    };
    this.ensureCurrentDay({ notify: false });
    this.saveState({ notify });
  }

  // --- دوال تبديل النمط والحساب ---
  setRole(role) {
    this.state.currentRole = role; // 'client' or 'coach'
    this.saveState();
  }

  restoreState(newState) {
    if (!newState || typeof newState !== 'object') {
      throw new Error('بيانات النسخة الاحتياطية غير صالحة');
    }
    this.state = newState;
    this.ensureCurrentDay({ notify: false });
    this.saveState();
  }

  getUserId() {
    const authId = this.state?.auth?.user?.id;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (authId && uuidRegex.test(authId)) {
      return authId;
    }
    return getOrCreateGuestUserId();
  }

  setUserProfile(profile, { skipSync = false } = {}) {
    this.state.userProfile = { ...this.state.userProfile, ...profile };
    if (profile.birthDate !== undefined) {
      this.state.userProfile.age = profile.birthDate ? calculateAge(profile.birthDate) : 0;
    }
    if (profile.targetCalories !== undefined) {
      this.state.today.targetCalories = profile.targetCalories;
      if (profile.targetProtein !== undefined) this.state.today.targetProtein = profile.targetProtein;
      if (profile.targetCarbs !== undefined) this.state.today.targetCarbs = profile.targetCarbs;
      if (profile.targetFats !== undefined) this.state.today.targetFats = profile.targetFats;
      if (profile.targetWaterLiters !== undefined) this.state.today.targetWaterLiters = profile.targetWaterLiters;
      if (profile.targetGlasses !== undefined) this.state.today.targetGlasses = profile.targetGlasses;
    }
    if (profile.nutritionPlanPreferences) {
      this.state.nutritionPlanPreferences = {
        ...(this.state.nutritionPlanPreferences || {}),
        ...profile.nutritionPlanPreferences
      };
    }
    this.saveState();

    if (!skipSync) {
      const userId = this.getUserId();
      if (userId) {
        syncService.syncProfile(userId, this.state.userProfile);
      }
    }
  }

  loginUser(userObj, provider = 'email', token = null, { loadRemote = true } = {}) {
    if (!this.state.auth) {
      this.state.auth = {};
    }
    this.state.auth = {
      isAuthenticated: true,
      user: userObj,
      provider: provider,
      token: token,
      lastLoginAt: new Date().toISOString()
    };

    if (userObj) {
      if (userObj.name) this.state.userProfile.name = userObj.name;
      if (userObj.email) this.state.userProfile.email = userObj.email;
      this.state.userProfile.provider = provider;
      if (userObj.avatarUrl) this.state.userProfile.avatarUrl = userObj.avatarUrl;
    }

    this.saveState();
    this.notify();

    if (loadRemote && userObj?.id) {
      syncService.loadUserData(userObj.id);
    }
  }

  registerUser(userObj, provider = 'email') {
    this.loginUser(userObj, provider);
  }

  logoutUser() {
    this.state.auth = {
      isAuthenticated: false,
      user: null,
      provider: null,
      token: null
    };
    this.saveState();
    this.notify();
  }

  getMetabolicSnapshot(now = new Date()) {
    const inputs = metabolicInputs(this.state, wtLoad());
    return estimateAdaptiveExpenditure({ ...inputs, profile: this.state.userProfile || {}, endDate: localDate(now) });
  }

  setNutritionDayStatus(date, status, calories = null) {
    if (dayNumber(date) === null || date > localDate() || !['complete','estimated','partial','missing','fasting'].includes(status)) throw new Error('بيانات اليوم غير صالحة');
    this.state.metabolicData ||= {};
    this.state.metabolicData.nutritionDays ||= {};
    const known = metabolicInputs(this.state).nutrition.find(r => r.date === date)?.calories;
    const value = calories === null ? known : Number(calories);
    if (status === 'fasting' && Number(known) > 0 && calories === null) throw new Error('يوجد طعام مسجل؛ لا يمكن اعتبار اليوم صيامًا بصفر سعرة');
    if (['complete','estimated'].includes(status) && !(value > 0 && value <= 15000)) throw new Error('أدخل مجموع سعرات اليوم أولًا');
    if (calories !== null && (!Number.isFinite(value) || value < 0 || value > 15000)) throw new Error('السعرات غير صالحة');
    this.state.metabolicData.nutritionDays[date] = { status, confirmedAt: new Date().toISOString(), ...(calories !== null || status === 'fasting' ? { calories: status === 'fasting' ? 0 : value } : {}) };
    this.saveState();
  }

  invalidateNutritionDay(date) {
    const days = this.state.metabolicData?.nutritionDays;
    if (days?.[date]) days[date] = { status: 'partial' };
  }

  saveMetabolicSettings(settings) {
    this.state.userProfile = { ...this.state.userProfile, ...settings };
    // A changed activity/RMR baseline resets calibration, never reuses an unrelated prior.
    this.state.metabolicData ||= {};
    delete this.state.metabolicData.estimate;
    this.recalculateTargetsFromProfile();
  }

  importMetabolicHistory(rows) {
    if (!Array.isArray(rows) || rows.some(r => dayNumber(r.date) === null || r.date > localDate())) throw new Error('توجد تواريخ مستقبلية أو غير صالحة');
    this.state.metabolicData ||= {};
    this.state.metabolicData.nutritionDays ||= {};
    const weights = [...(this.state.metabolicData.weights || [])];
    for (const row of rows) {
      if (row.weight) weights.push({ date: row.date, weight: row.weight });
      if (row.calories !== undefined) this.state.metabolicData.nutritionDays[row.date] = { calories: row.calories, status: row.status, source: 'csv_import' };
    }
    this.state.metabolicData.weights = normalizeWeights(weights);
    this.saveState();
  }

  addMetabolicEvent(type) {
    if (!['carb_change','creatine','illness','fluid_change'].includes(type)) throw new Error('حدث غير صالح');
    this.state.metabolicData ||= {};
    this.state.metabolicData.events = [...(this.state.metabolicData.events || []), { type, date: localDate() }].slice(-100);
    this.saveState();
  }

  recalculateTargetsFromProfile({ weeklyAdjustment = false } = {}) {
    const p = this.state.userProfile || {};
    const mode = p.targetMode || 'manual'; // Preserve legacy/manual targets until coached mode is selected.
    const effective = { ...p, weight: p.currentWeight || p.weight || 70, targetMode: mode };
    if (mode === 'coached') {
      delete effective.requestedTargetCalories;
      delete effective.dailyCalorieDeficit;
      delete effective.dailyCalorieSurplus;
      effective.adaptiveTdee = this.state.metabolicData?.estimate?.tdee;
    } else effective.requestedTargetCalories = p.targetCalories || p.requestedTargetCalories;
    let targets = calculateNutritionTargets(effective);
    if (weeklyAdjustment && p.targetCalories > 0) {
      const limited = Math.max(p.targetCalories - 150, Math.min(p.targetCalories + 150, targets.targetCalories));
      targets = calculateNutritionTargets({ ...effective, requestedTargetCalories: limited, calorieAdjustmentOverride: true });
    }
    Object.assign(p, { targetCalories: targets.targetCalories, requestedTargetCalories: targets.targetCalories,
      targetProtein: targets.protein, targetCarbs: targets.carbs, targetFats: targets.fats,
      bmr: targets.bmr, rmr: targets.rmr, rmrMethodUsed: targets.rmrMethod, maintenanceCalories: targets.tdee,
      targetWaterLiters: targets.waterMl / 1000, targetGlasses: targets.waterGlasses, macroWarnings: targets.macroWarnings, floorApplied: targets.floorApplied, goalReached: targets.goalReached });
    this.state.userProfile = p;
    const trainingDays = (p.metabolicTrainingDays || []).map(Number);
    const budget = distributeWeeklyCalories(targets.targetCalories, {
      trainingDays, shift: p.calorieShift || 0,
      floor: mode === 'coached' ? (p.gender === 'female' ? 1200 : 1500) : 0
    });
    this.state.metabolicData ||= {};
    this.state.metabolicData.weeklyCalories = budget;
    Object.assign(this.state.today, { targetCalories: budget[(new Date().getDay() + 1) % 7],
      targetProtein: targets.protein, targetCarbs: targets.carbs, targetFats: targets.fats,
      targetWaterLiters: targets.waterMl / 1000 });
    if (this.state.today.targetCalories !== targets.targetCalories) {
      const shifted = allocateMacros(this.state.today.targetCalories, effective);
      Object.assign(this.state.today, { targetProtein: shifted.protein, targetCarbs: shifted.carbs, targetFats: shifted.fats });
    }
    this.state.nutritionPlan = null;
    this.saveState();
    return targets;
  }

  // --- تتبع شرب الماء ---
  addWaterCup(amountMl = 250) {
    this.ensureCurrentDay({ notify: false });
    const currentLiters = this.state.today.consumedWaterLiters || 0;
    const newLiters = Math.round((currentLiters + (amountMl / 1000)) * 10) / 10;
    this.state.today.consumedWaterLiters = newLiters;
    const glassDelta = amountMl >= 400 ? 2 : 1;
    this.state.today.consumedGlasses = (this.state.today.consumedGlasses || 0) + glassDelta;
    this.saveState();

    const userId = this.getUserId();
    if (userId) {
      syncService.syncWaterLog(userId, Math.round(newLiters * 1000), this.state.today.consumedGlasses, this.state.today.targetGlasses || 10);
    }
  }

  undoWaterCup(amountMl = 250) {
    this.ensureCurrentDay({ notify: false });
    const currentLiters = this.state.today.consumedWaterLiters || 0;
    if (currentLiters <= 0 && (!this.state.today.consumedGlasses || this.state.today.consumedGlasses <= 0)) return;
    const newLiters = Math.max(0, Math.round((currentLiters - (amountMl / 1000)) * 10) / 10);
    this.state.today.consumedWaterLiters = newLiters;
    const glassDelta = amountMl >= 400 ? 2 : 1;
    this.state.today.consumedGlasses = Math.max(0, (this.state.today.consumedGlasses || glassDelta) - glassDelta);
    this.saveState();

    const userId = this.getUserId();
    if (userId) {
      syncService.syncWaterLog(userId, Math.round(newLiters * 1000), this.state.today.consumedGlasses, this.state.today.targetGlasses || 10);
    }
  }

  // --- تتبع المكملات القديم والتوافق ---
  toggleSupplement(suppId, timeOfDay = 'morning') {
    this.ensureCurrentDay({ notify: false });
    const supp = this.state.supplementsSchedule.find(s => s.id === suppId);
    if (supp && supp.schedule && supp.schedule[timeOfDay]) {
      const isTaken = !supp.schedule[timeOfDay].taken;
      supp.schedule[timeOfDay].taken = isTaken;
      supp.schedule[timeOfDay].time = isTaken ? new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' }) : null;
      this.saveState();
    }
  }

  // --- إدارة مكملات Daily Stack المتقدمة (من supplments.html) ---
  getActiveDate() {
    return localDate();
  }

  getDailyStackItems() {
    try {
      const stored = localStorage.getItem('daily_stack_items_v2');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    const defaultItems = [
      { id: 'item_creatine', name: 'Creatine monohydrate / كرياتين مونوهيدرات', dose: '5g', window: 'anytime', note: 'يومياً — الاستمرارية أهم من التوقيت' },
      { id: 'item_omega3', name: 'Omega-3 / أوميغا 3', dose: '2–3g EPA+DHA', window: 'lunch', note: 'مع أكبر وجبة دهنية' },
      { id: 'item_vitd3', name: 'Vitamin D3 / فيتامين د3', dose: '2000–5000 IU', window: 'lunch', note: 'يذوب في الدهون — يؤخذ مع أكبر وجبة' },
      { id: 'item_mag', name: 'Magnesium glycinate / مغنيسيوم غلايسينات', dose: '200–400mg', window: 'evening', note: 'قبل النوم بـ 30–60 دقيقة للاسترخاء' }
    ];
    this.setDailyStackItems(defaultItems);
    return defaultItems;
  }

  setDailyStackItems(items) {
    try {
      localStorage.setItem('daily_stack_items_v2', JSON.stringify(items));
    } catch (e) {}
    this.syncSupplementsToSchedule(items);
    this.saveState();
  }

  getDailyStackTaken() {
    const key = 'daily_stack_taken_v2:' + this.getActiveDate();
    try {
      const stored = localStorage.getItem(key);
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return {};
  }

  setDailyStackTaken(map) {
    const key = 'daily_stack_taken_v2:' + this.getActiveDate();
    try {
      localStorage.setItem(key, JSON.stringify(map));
    } catch (e) {}
    this.syncSupplementsToSchedule(this.getDailyStackItems());
    this.saveState();
  }

  getDailyStackLow() {
    try {
      const stored = localStorage.getItem('daily_stack_low_v2');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return [];
  }

  setDailyStackLow(list) {
    try {
      localStorage.setItem('daily_stack_low_v2', JSON.stringify(list));
    } catch (e) {}
    this.notify();
  }

  addDailyStackItem(name, dose, windowKey = 'anytime', note = '') {
    const items = this.getDailyStackItems();
    const newItem = {
      id: 'item_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      name: name.trim(),
      dose: dose.trim(),
      window: windowKey,
      note: note.trim()
    };
    items.push(newItem);
    this.setDailyStackItems(items);
    return newItem;
  }

  toggleDailyStackTaken(itemId) {
    this.ensureCurrentDay({ notify: false });
    const taken = this.getDailyStackTaken();
    if (taken[itemId]) {
      delete taken[itemId];
    } else {
      taken[itemId] = Date.now();
    }
    this.setDailyStackTaken(taken);
  }

  toggleDailyStackLow(itemId) {
    const low = this.getDailyStackLow();
    const updated = low.includes(itemId) ? low.filter(id => id !== itemId) : [...low, itemId];
    this.setDailyStackLow(updated);
  }

  deleteDailyStackItem(itemId) {
    const items = this.getDailyStackItems().filter(i => i.id !== itemId);
    this.setDailyStackItems(items);
    const taken = this.getDailyStackTaken();
    delete taken[itemId];
    this.setDailyStackTaken(taken);
    const low = this.getDailyStackLow().filter(id => id !== itemId);
    this.setDailyStackLow(low);
  }

  updateDailyStackItem(itemId, field, value) {
    const items = this.getDailyStackItems();
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    if (field === 'name' && value.trim()) item.name = value.trim();
    if (field === 'meta') {
      const parts = value.trim().split(/\s*·\s*/);
      item.dose = parts[0] || '';
      item.note = parts.slice(1).join(' · ');
    }
    this.setDailyStackItems(items);
  }

  syncSupplementsToSchedule(items) {
    const taken = this.getDailyStackTaken();
    this.state.supplementsSchedule = items.map(item => {
      const isTaken = !!taken[item.id];
      return {
        id: item.id,
        nameAr: item.name,
        dose: item.dose,
        timing: item.window,
        verifiedSource: item.note || 'مضاف يدوياً',
        schedule: {
          morning: { taken: isTaken, time: isTaken ? 'تم' : null },
          evening: { taken: isTaken, time: isTaken ? 'تم' : null }
        }
      };
    });
  }

  getWaterCoachConfig() {
    try {
      const stored = localStorage.getItem('water_coach_cfg_v1');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return {
      appTitle: 'Water Coach',
      unit: 'glass',
      bottleMl: 500,
      glassMl: 250,
      weightKg: this.state.userProfile?.currentWeight || 75,
      weightUnit: 'kg',
      age: 25,
      sex: 'm',
      activityHrsPerWeek: 5,
      caffeineMgPerDay: 200,
      substances: []
    };
  }

  saveWaterCoachConfig(cfg) {
    try {
      localStorage.setItem('water_coach_cfg_v1', JSON.stringify(cfg));
    } catch (e) {}
    this.notify();
  }

  // --- تعديل هدف السعرات والماكروز يدوياً ---
  setTargetCalories(newCalories, customMacros = null) {
    if (this.state.userProfile) this.state.userProfile.targetMode = 'manual';
    if (this.state.metabolicData) delete this.state.metabolicData.weeklyCalories;
    const target = Math.max(200, Math.round(Number(newCalories) || 2000));
    this.state.today.targetCalories = target;
    if (this.state.today.dailyTargetCalories !== undefined) {
      this.state.today.dailyTargetCalories = target;
    }

    if (customMacros && (customMacros.protein !== undefined || customMacros.carbs !== undefined || customMacros.fats !== undefined)) {
      if (customMacros.protein !== undefined) this.state.today.targetProtein = Math.max(0, Math.round(Number(customMacros.protein) || 0));
      if (customMacros.carbs !== undefined) this.state.today.targetCarbs = Math.max(0, Math.round(Number(customMacros.carbs) || 0));
      if (customMacros.fats !== undefined) this.state.today.targetFats = Math.max(0, Math.round(Number(customMacros.fats) || 0));
    } else {
      // إعادة توزيع الماكروز تلقائياً بنسب علمية متوازنة وتطابق رياضي تام
      const userWeight = this.state.userProfile?.currentWeight || 75;
      const userGoal = this.state.userProfile?.goal || 'fat_loss';
      const targets = calculateNutritionTargets({
        ...this.state.userProfile,
        currentWeight: userWeight,
        weight: userWeight,
        goal: userGoal,
        requestedTargetCalories: target
      });

      this.state.today.targetProtein = targets.protein;
      this.state.today.targetFats = targets.fats;
      this.state.today.targetCarbs = targets.carbs;
    }

    if (this.state.userProfile) {
      this.state.userProfile.targetCalories = target;
      this.state.userProfile.requestedTargetCalories = target;
      this.state.userProfile.targetProtein = this.state.today.targetProtein;
      this.state.userProfile.targetCarbs = this.state.today.targetCarbs;
      this.state.userProfile.targetFats = this.state.today.targetFats;
    }

    this.saveState();
  }

  // --- تتبع الطاقة اليومية (1-5) ---
  setEnergyLevel(level) {
    this.ensureCurrentDay({ notify: false });
    this.state.today.energyLevel = Math.max(1, Math.min(5, level));
    this.saveState();

    const userId = this.getUserId();
    if (userId) {
      syncService.syncEnergyLog(userId, this.state.today.energyLevel);
    }
  }

  // --- إدارة جلسة التمرين النشطة ---
  startWorkoutSession() {
    this.ensureCurrentDay({ notify: false });
    this.state.today.workoutStatus = 'in_progress';
    this.state.activeWorkoutSession.startedAtTimestamp = Date.now() - (this.state.activeWorkoutSession.elapsedSeconds * 1000);
    this.saveState();
  }

  updateWorkoutSet(setIndex, field, value) {
    this.ensureCurrentDay({ notify: false });
    const exercise = this.state.activeWorkoutSession.currentExercise;
    if (exercise && exercise.sets[setIndex]) {
      exercise.sets[setIndex][field] = value;
      this.saveState();
    }
  }

  toggleSetCompletion(setIndex) {
    this.ensureCurrentDay({ notify: false });
    const exercise = this.state.activeWorkoutSession.currentExercise;
    if (exercise && exercise.sets[setIndex]) {
      exercise.sets[setIndex].completed = !exercise.sets[setIndex].completed;
      this.saveState();
    }
  }

  addWorkoutSet() {
    this.ensureCurrentDay({ notify: false });
    const exercise = this.state.activeWorkoutSession.currentExercise;
    if (exercise) {
      const lastSet = exercise.sets[exercise.sets.length - 1] || { weight: 70, reps: 10, rpe: 8 };
      exercise.sets.push({
        setNumber: exercise.sets.length + 1,
        weight: lastSet.weight,
        reps: lastSet.reps,
        rpe: lastSet.rpe,
        completed: false
      });
      this.saveState();
    }
  }

  logWorkoutPain(location, severity, note) {
    this.ensureCurrentDay({ notify: false });
    this.state.activeWorkoutSession.painReports.push({
      timestamp: new Date().toISOString(),
      location,
      severity,
      note
    });
    this.saveState();
  }

  finishWorkoutSession(customData = null) {
    this.ensureCurrentDay({ notify: false });
    if (this.state.today.workoutStatus === 'completed' && !customData) return; // منع إنهاء الجلسة مرتين
    this.state.today.workoutStatus = 'completed';
    this.state.today.isWorkoutCompleted = true;

    const now = new Date();
    const dateLabel = new Intl.DateTimeFormat('ar-EG', { weekday: 'long', day: 'numeric', month: 'short' }).format(now);
    const active = this.state.activeWorkoutSession;
    const currentEx = active?.currentExercise;
    const completedSets = (currentEx?.sets || []).filter(s => s.completed);
    const bestSetObj = completedSets.length > 0 
      ? completedSets.reduce((max, s) => (Number(s.weight) > Number(max.weight) ? s : max), completedSets[0])
      : { weight: 70, reps: 10 };

    const newSessionRecord = {
      id: 'hist_' + Date.now(),
      title: customData?.title || active?.sessionNameAr || 'صدر وترايسبس',
      dateLabel: customData?.dateLabel || dateLabel,
      durationMinutes: customData?.durationMinutes || Math.max(1, Math.round((active?.elapsedSeconds || 1800) / 60)),
      totalVolumeKg: customData?.totalVolumeKg || 8450,
      totalSets: customData?.totalSets || Math.max(currentEx?.sets?.length || 3, 16),
      totalReps: customData?.totalReps || 160,
      exercises: filterStrongestExercisePerMuscle(customData?.exercises || [
        {
          nameAr: currentEx?.nameAr || 'ضغط بار مستوي (Bench Press)',
          bestSet: `${bestSetObj.weight || 72.5} كغ × ${bestSetObj.reps || 9} تكرارات`,
          setsCount: currentEx?.sets?.length || 3
        },
        {
          nameAr: 'ضغط دمبلز مائل (Incline DB Press)',
          bestSet: '32 كغ × 10 تكرارات',
          setsCount: 3
        },
        {
          nameAr: 'ضغط ترايسبس بالكيبل (Triceps Pushdown)',
          bestSet: '35 كغ × 12 تكرار',
          setsCount: 4
        }
      ])
    };

    if (!Array.isArray(this.state.workoutHistory)) {
      this.state.workoutHistory = [];
    }
    this.state.workoutHistory.unshift(newSessionRecord);

    // حفظ الجلسة في سجل تاريخ التمارين
    try {
      if (typeof localStorage !== 'undefined') {
        const existing = JSON.parse(localStorage.getItem('neon_workout_history_v1') || '[]');
        existing.unshift(newSessionRecord);
        localStorage.setItem('neon_workout_history_v1', JSON.stringify(existing.slice(0, 50)));
      }
    } catch (e) {}

    // مزامنة الجلسة في قاعدة البيانات سحابياً
    if (!customData?.skipSync) {
      const userId = this.getUserId();
      if (userId) {
        syncService.syncWorkoutLog(userId, {
          title: newSessionRecord.title,
          programType: 'workout',
          date: localDate(),
          durationMinutes: newSessionRecord.durationMinutes,
          totalVolumeKg: newSessionRecord.totalVolumeKg,
          exercises: newSessionRecord.exercises,
          notes: `الجولات: ${newSessionRecord.totalSets}، التكرارات: ${newSessionRecord.totalReps}`
        });
      }
    }

    this.saveState();
  }

  getWorkoutHistory() {
    // 1. التحقق من السجلات في state أولاً
    if (Array.isArray(this.state.workoutHistory) && this.state.workoutHistory.length > 0) {
      return this.state.workoutHistory.map(session => ({
        ...session,
        exercises: filterStrongestExercisePerMuscle(session.exercises || [])
      }));
    }

    try {
      if (typeof localStorage !== 'undefined') {
        // 2. التحقق من السجلات المحلية المسجلة عبر التطبيق
        const keys = ['neon_workout_history_v1', 'fortyDay_workout_history_v1', 'hasm_workout_history_v1'];
        for (const key of keys) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) {
              return parsed.map(item => ({
                id: item.id || 'hist_' + Math.random().toString(36).slice(2, 7),
                title: item.title || item.sessionNameAr || 'جلسة تدريبية',
                dateLabel: item.dateLabel || 'مؤخراً',
                durationMinutes: item.durationMinutes || (item.durationSeconds ? Math.round(item.durationSeconds / 60) : 0),
                totalVolumeKg: item.totalVolumeKg || item.totalVolume || 0,
                totalSets: item.totalSets || (item.exercises ? item.exercises.reduce((sum, e) => sum + (e.sets || 3), 0) : 0),
                totalReps: item.totalReps || 0,
                exercises: filterStrongestExercisePerMuscle((item.exercises || []).map(ex => ({
                  nameAr: ex.nameAr || ex.title || 'تمرين',
                  bestSet: ex.bestSet || (ex.bestKg ? `${ex.bestKg} كغ × ${ex.bestReps || '-'} تكرار` : (ex.sets ? `${ex.sets} جولات` : '')),
                  setsCount: ex.setsCount || ex.sets || 0
                })))
              }));
            }
          }
        }
      }
    } catch (e) {}

    // لا يوجد سجل بعد — يُعاد مصفوفة فارغة
    return [];
  }

  // --- إدارة أسماء وتصنيفات الوجبات المخصصة ---
  getCustomMealNames() {
    if (!Array.isArray(this._customMealNames)) {
      this._customMealNames = [];
      try {
        if (typeof localStorage !== 'undefined') {
          const raw = localStorage.getItem('neon_custom_meal_names');
          if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) this._customMealNames = parsed;
          }
        }
      } catch (e) {}
    }
    return [...this._customMealNames];
  }

  saveCustomMealName(name) {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    const defaults = ['فطور', 'غداء', 'عشاء', 'سناك'];
    if (defaults.includes(trimmed)) return;

    const current = this.getCustomMealNames();
    if (!current.includes(trimmed)) {
      current.push(trimmed);
      this._customMealNames = current;
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('neon_custom_meal_names', JSON.stringify(current));
        }
      } catch (e) {}
    }
  }

  deleteCustomMealName(name) {
    const current = this.getCustomMealNames();
    const updated = current.filter(n => n !== name);
    this._customMealNames = updated;
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('neon_custom_meal_names', JSON.stringify(updated));
      }
    } catch (e) {}
  }

  getAllMealCategoryNames() {
    const defaults = ['فطور', 'غداء', 'عشاء', 'سناك'];
    return [...defaults, ...this.getCustomMealNames()];
  }

  // --- تسجيل الوجبات وتحديث المجاميع ---
  logMeal(meal) {
    this.ensureCurrentDay({ notify: false });
    const isUuid = meal.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(meal.id);
    const newLog = {
      id: isUuid ? meal.id : generateUUID(),
      date: meal.date || localDate(),
      time: meal.time || new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' }),
      titleAr: meal.titleAr || meal.name || 'وجبة جديدة',
      calories: Number(meal.calories ?? meal.totalCalories) || 0,
      protein: Number(meal.protein ?? meal.totalProtein) || 0,
      carbs: Number(meal.carbs ?? meal.totalCarbs) || 0,
      fats: Number(meal.fats ?? meal.totalFats) || 0,
      items: meal.items || []
    };

    this.state.loggedMeals.push(newLog);
    this.invalidateNutritionDay(newLog.date);
    this.recalculateDailyNutrition();
    this.saveState();

    const userId = this.getUserId();
    if (userId) {
      syncService.syncMealLog(userId, {
        id: newLog.id,
        name: newLog.titleAr,
        calories: newLog.calories,
        protein: newLog.protein,
        carbs: newLog.carbs,
        fats: newLog.fats,
        items: newLog.items,
        date: newLog.date,
      });
    }

    return newLog;
  }

  async updateLoggedMeal(logId, updatedData) {
    const index = (this.state.loggedMeals || []).findIndex(m => m.id === logId);
    if (index !== -1) {
      this.invalidateNutritionDay(this.state.loggedMeals[index].date);
      this.state.loggedMeals[index] = { ...this.state.loggedMeals[index], ...updatedData };
      this.invalidateNutritionDay(this.state.loggedMeals[index].date);
      this.recalculateDailyNutrition();
      this.saveState();
    }
    const userId = this.getUserId();
    if (userId) {
      try {
        await syncService.persistManualMeal(userId, logId, updatedData);
      } catch (err) {
        console.warn('فشل مزامنة تعديل الوجبة سحابياً:', err);
      }
    }
  }

  async deleteLoggedMeal(logId) {
    this.invalidateNutritionDay((this.state.loggedMeals || []).find(m => m.id === logId)?.date);
    this.state.loggedMeals = (this.state.loggedMeals || []).filter(m => m.id !== logId);
    this.recalculateDailyNutrition();
    this.saveState();

    const userId = this.getUserId();
    if (userId) {
      try {
        await syncService.persistManualMeal(userId, logId);
      } catch (err) {
        console.warn('فشل مزامنة حذف الوجبة سحابياً:', err);
      }
    }
  }

  // --- الوجبات المحفوظة (المفضلة) ---
  saveToFavorites(meal) {
    if (!this.state.savedMeals) this.state.savedMeals = [];
    // تجنب الحفظ المكرر بنفس الاسم والسعرات
    const isDuplicate = this.state.savedMeals.some(
      m => m.titleAr === meal.titleAr && m.calories === meal.calories
    );
    if (isDuplicate) return { success: false, reason: 'duplicate' };
    const saved = {
      id: generateUUID(),
      savedAt: new Date().toISOString(),
      titleAr: meal.titleAr || 'وجبة محفوظة',
      calories: meal.calories || 0,
      protein: meal.protein || 0,
      carbs: meal.carbs || 0,
      fats: meal.fats || 0,
      items: meal.items || []
    };
    this.state.savedMeals.unshift(saved);
    this.saveState();
    return { success: true, saved };
  }

  deleteSavedMeal(savedId) {
    if (!this.state.savedMeals) return;
    this.state.savedMeals = this.state.savedMeals.filter(m => m.id !== savedId);
    this.saveState();
  }

  // --- إدارة الوصفات المفضلة (Favorite Recipes) ---
  getFavoriteRecipeIds() {
    if (!Array.isArray(this.state.favoriteRecipeIds)) {
      this.state.favoriteRecipeIds = [];
    }
    return this.state.favoriteRecipeIds;
  }

  isRecipeFavorite(recipeId) {
    if (!Array.isArray(this.state.favoriteRecipeIds)) return false;
    return this.state.favoriteRecipeIds.includes(recipeId);
  }

  toggleFavoriteRecipe(recipeId) {
    if (!Array.isArray(this.state.favoriteRecipeIds)) {
      this.state.favoriteRecipeIds = [];
    }
    const idx = this.state.favoriteRecipeIds.indexOf(recipeId);
    let isFav = false;
    if (idx > -1) {
      this.state.favoriteRecipeIds.splice(idx, 1);
      isFav = false;
    } else {
      this.state.favoriteRecipeIds.push(recipeId);
      isFav = true;
    }
    this.saveState();
    return isFav;
  }

  recalculateDailyNutrition() {
    let cals = 0;
    let p = 0;
    let c = 0;
    let f = 0;
    for (const meal of this.state.loggedMeals) {
      if (meal.date && meal.date !== this.state.today.date) continue;
      cals += meal.calories || 0;
      p += meal.protein || 0;
      c += meal.carbs || 0;
      f += meal.fats || 0;
    }
    this.state.today.consumedCalories = cals;
    this.state.today.consumedProtein = p;
    this.state.today.consumedCarbs = c;
    this.state.today.consumedFats = f;
  }

  // --- تبديل وجبة في الخطة اليومية ---
  swapMealPlan(mealId, newSwap) {
    const meal = this.state.mealPlan.meals.find(m => m.id === mealId);
    if (meal) {
      meal.titleAr = newSwap.titleAr;
      meal.calories = newSwap.calories;
      meal.protein = newSwap.protein;
      meal.carbs = newSwap.carbs;
      meal.fats = newSwap.fats;
      this.saveState();
    }
  }

  // --- إدارة خطة التغذية الأسبوعية المولدة (7 أيام) ---
  getNutritionPlan() {
    return this.state.nutritionPlan || null;
  }

  setNutritionPlan(plan) {
    this.state.nutritionPlan = plan;
    this.saveState();
  }

  getNutritionPlanPreferences() {
    return this.state.nutritionPlanPreferences || null;
  }

  setNutritionPlanPreferences(preferences) {
    this.state.nutritionPlanPreferences = {
      ...(this.state.nutritionPlanPreferences || {}),
      ...preferences
    };
    this.saveState();
  }

  // --- المتابعة الأسبوعية ---
  submitWeeklyCheckin(checkinData) {
    const estimate = this.getMetabolicSnapshot();
    this.state.metabolicData ||= {};
    this.state.metabolicData.estimate = estimate;
    if (this.state.userProfile?.targetMode === 'coached' && estimate.calibrated && estimate.status !== 'holding' && !estimate.reasons.includes('NEXT_WEEKLY_UPDATE_PENDING')) this.recalculateTargetsFromProfile({ weeklyAdjustment: true });
    this.state.weeklyCheckin = {
      ...this.state.weeklyCheckin,
      ...checkinData,
      submittedAt: new Date().toISOString()
    };
    // تحديث الوزن الحالي في الملف
    if (checkinData.currentWeight) {
      this.state.userProfile.currentWeight = Number(checkinData.currentWeight);
    }
    this.saveState();
  }

  // --- تسجيل قياسات تقرير التقدم وInBody ---
  logProgressMeasurement({ weight, waistCm, benchPressKg }) {
    if (!this.state.progressReport) return;
    const rep = this.state.progressReport;
    if (weight !== undefined && weight !== '' && !isNaN(Number(weight))) {
      const w = Number(weight);
      this.state.metabolicData ||= {};
      this.state.metabolicData.weights = normalizeWeights([...(this.state.metabolicData.weights || []), { date: localDate(), weight: w }]);
      rep.currentDay.weight = w;
      if (this.state.userProfile) {
        this.state.userProfile.currentWeight = w;
      }
      rep.weightChangeKg = Math.round((w - rep.firstDay.weight) * 10) / 10;
      if (!rep.weightTrendData) rep.weightTrendData = [];
      rep.weightTrendData.push({ day: rep.periodDays || 90, weight: w });
    }
    if (waistCm !== undefined && waistCm !== '' && !isNaN(Number(waistCm))) {
      rep.currentDay.waistCm = Number(waistCm);
    }
    if (benchPressKg !== undefined && benchPressKg !== '' && !isNaN(Number(benchPressKg))) {
      const b = Number(benchPressKg);
      rep.currentDay.benchPressKg = b;
      if (!rep.strengthTrendData) rep.strengthTrendData = [];
      rep.strengthTrendData.push({ day: rep.periodDays || 90, weight: b });
    }

    const userId = this.getUserId();
    if (userId) {
      syncService.syncInbodyRecord(userId, {
        weight: weight !== undefined && weight !== '' && !isNaN(Number(weight)) ? Number(weight) : null,
        waistCm: waistCm !== undefined && waistCm !== '' && !isNaN(Number(waistCm)) ? Number(waistCm) : null,
        notes: benchPressKg ? `بنش برس: ${benchPressKg} كغ` : ''
      });
    }

    this.saveState();
  }

  updateInBodyResult({ bodyFatPercentage, skeletalMuscleMassKg, visceralFatLevel, provider }) {
    if (!this.state.progressReport) return;
    const now = new Date();
    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    this.state.progressReport.inBodyResult = {
      hasResult: true,
      testDate: dateStr,
      provider: provider || 'InBody Analysis',
      bodyFatPercentage: Number(bodyFatPercentage) || 0,
      skeletalMuscleMassKg: Number(skeletalMuscleMassKg) || 0,
      visceralFatLevel: Number(visceralFatLevel) || 0
    };

    const userId = this.getUserId();
    if (userId) {
      syncService.syncInbodyRecord(userId, {
        bodyFatPct: Number(bodyFatPercentage) || null,
        muscleMassKg: Number(skeletalMuscleMassKg) || null,
        visceralFat: Number(visceralFatLevel) || null,
        notes: provider || 'InBody Analysis'
      });
    }

    this.saveState();
  }

  // --- اعتماد خطة من المدرب ---
  approveClientPlan(clientId, notes = '') {
    this.state.mealPlan.status = 'approved_by_coach';
    this.state.mealPlan.approvedAt = new Date().toISOString();
    this.state.progressReport.coachNotes = notes || 'تمت مراجعة واعتماد الخطة بنجاح.';
    this.saveState();
  }

  // --- إدارة وحفظ ذاكرة محادثات الذكاء الاصطناعي ---
  getActionContext() {
    const taken = this.getDailyStackTaken();
    const supplementsSchedule = (this.state.supplementsSchedule || []).map(item => ({
      ...item, schedule: { morning: { ...item.schedule?.morning, taken: !!taken[item.id] }, evening: { ...item.schedule?.evening, taken: !!taken[item.id] } }
    }));
    return { ...this.state, actionSupplementTaken: taken, supplementsSchedule };
  }

  executeAgentTools(tools) {
    this.ensureCurrentDay({ notify: false });
    const context = this.getActionContext();
    const result = executeActionTools(context, tools);
    if (!result.changed) return result;
    const next = result.state;
    // Keep the existing standalone supplement and workout screens on the same records.
    const writes = [[STORAGE_KEY, JSON.stringify(next)]];
    if (JSON.stringify(next.actionSupplementTaken) !== JSON.stringify(context.actionSupplementTaken)) {
      writes.unshift(['daily_stack_taken_v2:' + this.getActiveDate(), JSON.stringify(next.actionSupplementTaken || {})]);
    }
    if (JSON.stringify(next.workoutHistory) !== JSON.stringify(context.workoutHistory)) {
      writes.unshift(['neon_workout_history_v1', JSON.stringify(next.workoutHistory || [])]);
    }
    const previous = [];
    try {
      if (typeof localStorage !== 'undefined') {
        for (const [key, value] of writes) {
          previous.push([key, localStorage.getItem(key)]);
          localStorage.setItem(key, value);
        }
      }
    } catch (error) {
      for (const [key, value] of previous.reverse()) {
        try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* Keep in-memory state unchanged. */ }
      }
      throw new Error('تعذر حفظ الإجراء في المتصفح. تحقق من مساحة التخزين وحاول مجدداً.');
    }
    this.state = next;
    // The chat updates its live panel without destroying the microphone session.
    return result;
  }

  getShoppingItems() { return shoppingItemsFor(this.state); }

  setShoppingItems(items) {
    this.state.shoppingItems = items;
    this.saveState();
  }

  getAiChatHistory() {
    if (!this.state.aiChatHistory) {
      this.state.aiChatHistory = [];
    }
    return this.state.aiChatHistory;
  }

  saveAiChatMessage(message) {
    if (!this.state.aiChatHistory) {
      this.state.aiChatHistory = [];
    }
    this.state.aiChatHistory.push({
      ...message,
      timestamp: message.timestamp || Date.now()
    });
    // الحفاظ على آخر 60 رسالة لتجنب التضخم مع الحفاظ على عمق الذاكرة
    if (this.state.aiChatHistory.length > 60) {
      this.state.aiChatHistory = this.state.aiChatHistory.slice(-60);
    }
    this.saveState({ notify: false });
  }

  clearAiChatHistory() {
    this.state.aiChatHistory = [];
    this.saveState();
  }
}

export const store = new Store();
